import * as React from 'react';
import { useDebounce } from '@avensia/excite-ui';
import type { ValidationPropType } from './use-validation';
import { useValidation } from './use-validation';

export type NumberFieldPropType = ValidationPropType & {
  min?: string | number;
  max?: string | number;
  onDebouncedUpdate?(value: number): Promise<unknown> | void;
  onUpdate?(value: number): void;
  onReduce?(): void;
  onAdd?(): void;
  multiplier?: number;
  debounceTimeout?: number;
};

export function useNumberField(
  inputRef: React.RefObject<HTMLInputElement | null>,
  externalValue: number,
  {
    error,
    validate,
    min,
    max,
    onAdd,
    onReduce,
    onDebouncedUpdate,
    onUpdate,
    multiplier = 1,
    debounceTimeout,
  }: NumberFieldPropType = {},
) {
  const [internalValue, setInternalValue] = React.useState(String(externalValue));
  const hasValueChangedRef = React.useRef(false);
  const valueInt = parseInt(internalValue);

  const willUpdateRef = React.useRef(false);

  const valueRef = React.useRef(externalValue);
  valueRef.current = externalValue;

  const queueRef = React.useRef<Promise<void> | undefined>(undefined);

  const [invalid, validationMessage, updateValidation, triggerInvalid] = useValidation(inputRef, error, validate);

  const updateValue = React.useCallback(
    (nextValue: number) => {
      willUpdateRef.current = true;
      const queue = queueRef.current;
      const currentQueue: Promise<void> | undefined = (queueRef.current = (async () => {
        await queue;

        hasValueChangedRef.current = true;
        if (nextValue !== valueRef.current) {
          let adjustedNextValue = nextValue;
          if (isNaN(adjustedNextValue)) {
            adjustedNextValue = valueRef.current;
          }
          if (typeof max === 'number' && adjustedNextValue > max) {
            adjustedNextValue = max;
          }
          if (typeof min === 'number' && adjustedNextValue < min) {
            adjustedNextValue = min;
          }
          if (adjustedNextValue !== nextValue) {
            setInternalValue(String(adjustedNextValue));
          }

          onUpdate?.(adjustedNextValue);

          try {
            await onDebouncedUpdate?.(adjustedNextValue);
          } catch {
            setInternalValue(String(externalValue));
          }
        }

        // In later TS this will fail since we are accessing ourself and that will yield an error
        // ===> Block-scoped variable 'currentQueue' used before its declaration.
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (currentQueue === queueRef.current) {
          willUpdateRef.current = false;
          if (inputRef.current) {
            updateValidation(inputRef.current);
          }
        }
      })());
    },
    [max, min, onUpdate, onDebouncedUpdate, externalValue, inputRef, updateValidation],
  );

  const previousDebounce = React.useRef<Promise<void>>(undefined);
  const debounced = useDebounce(updateValue, debounceTimeout);

  const debouncedUpdateValue = React.useCallback(
    async (nextValue: number) => {
      await queueRef.current;
      willUpdateRef.current = true;

      const debounce = debounced(nextValue);

      if (debounce !== previousDebounce.current) {
        previousDebounce.current = debounce;
      }

      return debounce;
    },
    [debounced],
  );

  React.useEffect(() => {
    if (!willUpdateRef.current) {
      setInternalValue(String(externalValue));
    }
  }, [externalValue]);

  const onChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInternalValue(e.currentTarget.value);
  }, []);

  const onBlur = React.useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      updateValue(parseInt(e.currentTarget.value));
    },
    [updateValue],
  );

  const onInvalid = React.useCallback(
    (e: React.InvalidEvent<HTMLInputElement>) => {
      triggerInvalid(e.currentTarget);
    },
    [triggerInvalid],
  );

  const onKeyPress = React.useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur();
    }
  }, []);

  const reduce = React.useCallback(async () => {
    let nextValue = valueInt - multiplier;

    if (typeof min === 'number' && nextValue < min) {
      nextValue = min;
    }

    setInternalValue(String(nextValue));
    debouncedUpdateValue(nextValue);
    onUpdate?.(nextValue);
    onReduce?.();
  }, [debouncedUpdateValue, min, multiplier, onReduce, onUpdate, valueInt]);

  const add = React.useCallback(async () => {
    let nextValue = valueInt + multiplier;

    if (typeof max === 'number' && nextValue > max) {
      nextValue = max;
    }

    setInternalValue(String(nextValue));
    debouncedUpdateValue(nextValue);
    onUpdate?.(nextValue);
    onAdd?.();
  }, [debouncedUpdateValue, max, multiplier, onAdd, onUpdate, valueInt]);

  return React.useMemo(
    () =>
      ({
        add,
        reduce,
        invalid,
        validationMessage,
        getHasValueChanged() {
          return hasValueChangedRef.current;
        },
        inputProps: {
          value: internalValue,
          min,
          max,
          onChange,
          onBlur,
          onInvalid,
          onKeyPress,
        },
      } as const),
    [add, internalValue, invalid, max, min, onBlur, onChange, onInvalid, onKeyPress, reduce, validationMessage],
  );
}
